/**
 * Cart utility functions for managing shopping cart state
 */

const CART_STORAGE_KEY = 'cart';

/**
 * Get cart from localStorage
 * @returns {Array} Cart items array
 */
export const getCart = () => {
  try {
    const savedCart = localStorage.getItem(CART_STORAGE_KEY);
    return savedCart ? JSON.parse(savedCart) : [];
  } catch (error) {
    console.error('Error loading cart from localStorage:', error);
    return [];
  }
};

/**
 * Save cart to localStorage
 * @param {Array} cart - Cart items array
 */
export const saveCart = (cart) => {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
  } catch (error) {
    console.error('Error saving cart to localStorage:', error);
  }
};

/**
 * Clear cart from localStorage
 */
export const clearCart = () => {
  try {
    localStorage.removeItem(CART_STORAGE_KEY);
    console.log('Cart cleared successfully');
  } catch (error) {
    console.error('Error clearing cart from localStorage:', error);
  }
};

/**
 * Add item to cart
 * @param {Array} currentCart - Current cart items
 * @param {Object} item - Item to add
 * @returns {Array} Updated cart
 */
export const addToCart = (currentCart, item) => {
  const existingItem = currentCart.find(cartItem => cartItem.id === item.id);
  
  if (existingItem) {
    return currentCart.map(cartItem => 
      cartItem.id === item.id 
        ? { ...cartItem, quantity: cartItem.quantity + item.quantity }
        : cartItem
    );
  } else {
    return [...currentCart, item];
  }
};

/**
 * Remove item from cart
 * @param {Array} currentCart - Current cart items
 * @param {string} itemId - ID of item to remove
 * @returns {Array} Updated cart
 */
export const removeFromCart = (currentCart, itemId) => {
  return currentCart.filter(item => item.id !== itemId);
};

/**
 * Update item quantity in cart
 * @param {Array} currentCart - Current cart items
 * @param {string} itemId - ID of item to update
 * @param {number} quantity - New quantity
 * @returns {Array} Updated cart
 */
export const updateCartQuantity = (currentCart, itemId, quantity) => {
  if (quantity <= 0) {
    return removeFromCart(currentCart, itemId);
  }
  
  return currentCart.map(item => 
    item.id === itemId 
      ? { ...item, quantity }
      : item
  );
};

/**
 * Calculate cart total
 * @param {Array} cart - Cart items array
 * @returns {number} Total amount
 */
export const calculateCartTotal = (cart) => {
  return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
};

/**
 * Get cart item count
 * @param {Array} cart - Cart items array
 * @returns {number} Total item count
 */
export const getCartItemCount = (cart) => {
  return cart.reduce((count, item) => count + item.quantity, 0);
};
